import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import {
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentDuplicateIcon,
  MinusIcon,
  PlusIcon,
  QrCodeIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  DEFAULT_MEDIA_ID,
  MAX_LENGTH_MM,
  MIN_LENGTH_MM,
  PRINTERS,
  PRINTER_DPI,
  PT_MM,
  SAFE_INSET_MM,
  autoLayout,
  clampLength,
  code39Data,
  elementText,
  fieldsForModule,
  fittedFontMm,
  hasTokens,
  labelDims,
  mediaById,
  mediaForPrinter,
  mmToDots,
  newElementId,
  resolveTemplate,
} from "./labelModel";
import { LabelElementContent, elementBoxStyle } from "./LabelContent";
import { printLabels } from "./labelPrint";
import "./label-designer.css";

const PREFS_KEY = "promang.labelDesigner.prefs";
const SNAP_MM = 0.5;

function readPrefs() {
  try {
    const raw = window.localStorage.getItem(PREFS_KEY);
    const prefs = raw ? JSON.parse(raw) : {};
    return prefs && typeof prefs === "object" ? prefs : {};
  } catch (_) {
    return {};
  }
}

function writePrefs(prefs) {
  try {
    window.localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch (_) {
    /* Private mode or blocked storage: preferences are a convenience only. */
  }
}

function initialSetup() {
  const prefs = readPrefs();
  const printer = PRINTERS.includes(prefs.printer) ? prefs.printer : PRINTERS[0];
  const allowed = mediaForPrinter(printer);
  const media = allowed.find((m) => m.id === prefs.mediaId) || mediaById(DEFAULT_MEDIA_ID);
  return { printer, mediaId: media.id, length: clampLength(prefs.length ?? media.len ?? 29) };
}

const snap = (v) => Math.round(v / SNAP_MM) * SNAP_MM;
const round1 = (v) => Math.round(v * 10) / 10;

function mediaLabel(m, t) {
  return m.cont
    ? t("labels.designer.mediaContinuous", { id: m.id, w: m.w })
    : t("labels.designer.mediaDieCut", { id: m.id, w: Math.min(m.w, m.h), h: Math.max(m.w, m.h) });
}

function Segmented({ options, value, onChange, label }) {
  return (
    <div className="pm-segmented pm-segmented--sm" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          title={o.title}
          aria-label={o.title}
          aria-pressed={o.value === value}
          className={`pm-segmented__option${o.value === value ? " is-active" : ""}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function IconButton({ label, onClick, children, danger, active, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={active === undefined ? undefined : active}
      disabled={disabled}
      className={`label-designer__icon-btn${danger ? " is-danger" : ""}${active ? " is-active" : ""}`}
    >
      {children}
    </button>
  );
}

/**
 * Label designer and printer (Claude Design «Etikettdesigner Dialog»).
 *
 * `loadRecords` returns (or resolves to) label records built with the helpers
 * in labelRecords.js. Labels print through the browser print dialog with the
 * page size set to the label, so the Brother QL driver gets one label per page.
 */
export function LabelDesignerDialog({ open, onClose, module = "product", loadRecords }) {
  const { t } = useTranslation();
  const [setup, setSetup] = useState(initialSetup);
  const { printer, mediaId, length } = setup;
  const media = mediaById(mediaId);
  const [W, H] = labelDims(media, length);

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [recIdx, setRecIdx] = useState(0);
  const [elements, setElements] = useState([]);
  const [selId, setSelId] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState({ w: 600, h: 400 });
  const [showGrid, setShowGrid] = useState(false);
  const [copies, setCopies] = useState(1);
  const [printing, setPrinting] = useState(false);
  const [notice, setNotice] = useState(null);

  const [areaNode, setAreaNode] = useState(null);
  const textRef = useRef(null);
  const loadSeq = useRef(0);
  const loadRef = useRef(loadRecords);
  loadRef.current = loadRecords;

  const fieldKeys = fieldsForModule(module);
  const record = records[recIdx] || {};
  const selected = elements.find((e) => e.id === selId) || null;

  const fetchRecords = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setLoadError(false);
    try {
      const result = await Promise.resolve(loadRef.current ? loadRef.current() : []);
      if (seq !== loadSeq.current) return;
      const list = Array.isArray(result) ? result.filter(Boolean) : [];
      setRecords(list);
      setRecIdx((i) => (i < list.length ? i : 0));
    } catch (_) {
      if (seq === loadSeq.current) setLoadError(true);
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, []);

  // Fresh start every time the dialog opens.
  useEffect(() => {
    if (!open) {
      loadSeq.current += 1;
      return;
    }
    const s = initialSetup();
    setSetup(s);
    setElements(autoLayout(module, ...labelDims(mediaById(s.mediaId), s.length)));
    setRecords([]);
    setRecIdx(0);
    setSelId(null);
    setZoom(1);
    setCopies(1);
    setNotice(null);
    setPrinting(false);
    fetchRecords();
  }, [open, module, fetchRecords]);

  useEffect(() => {
    if (open) writePrefs(setup);
  }, [open, setup]);

  // Track the canvas area for fit-to-view scaling. A callback ref is used
  // because the panel mounts inside a transition after this effect runs.
  useEffect(() => {
    if (!areaNode || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([entry]) =>
      setArea({ w: entry.contentRect.width, h: entry.contentRect.height })
    );
    ro.observe(areaNode);
    return () => ro.disconnect();
  }, [areaNode]);

  // Re-render once web fonts are in so text fitting measures the right glyphs.
  const [, setFontTick] = useState(0);
  useEffect(() => {
    if (!open || typeof document === "undefined" || !document.fonts?.ready) return;
    let alive = true;
    document.fonts.ready.then(() => alive && setFontTick((n) => n + 1));
    return () => {
      alive = false;
    };
  }, [open]);

  const scale = useMemo(() => {
    const fit = Math.min((area.w - 48) / W, (area.h - 96) / H, 12);
    return Math.max(1, fit) * zoom;
  }, [area, W, H, zoom]);

  const update = useCallback((id, patch) => {
    setElements((els) =>
      els.map((e) => (e.id === id ? { ...e, ...(typeof patch === "function" ? patch(e) : patch) } : e))
    );
  }, []);

  const relayout = (nextSetup) => {
    setSetup(nextSetup);
    setSelId(null);
    setElements(autoLayout(module, ...labelDims(mediaById(nextSetup.mediaId), nextSetup.length)));
  };

  const choosePrinter = (name) => {
    const allowed = mediaForPrinter(name);
    if (allowed.some((m) => m.id === mediaId)) setSetup({ ...setup, printer: name });
    else relayout({ ...setup, printer: name, mediaId: DEFAULT_MEDIA_ID });
  };

  const chooseMedia = (id) => {
    const next = mediaById(id);
    relayout({ ...setup, mediaId: next.id, length: next.cont ? next.len : length });
  };

  const deleteSelected = useCallback(() => {
    setElements((els) => els.filter((e) => e.id !== selId));
    setSelId(null);
  }, [selId]);

  // Arrow keys nudge, Delete removes (only when focus is not in a field).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || e.target?.isContentEditable) return;
      if (!selId) return;
      const step = e.shiftKey ? 5 : SNAP_MM;
      const move = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
      if (move) {
        e.preventDefault();
        update(selId, (x) => ({ x: round1(x.x + move[0]), y: round1(x.y + move[1]) }));
      } else if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        deleteSelected();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, selId, update, deleteSelected]);

  const startDrag = (e, el, mode) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    setSelId(el.id);
    const s = scale;
    const sx = e.clientX;
    const sy = e.clientY;
    const o = { x: el.x, y: el.y, w: el.w, h: el.h };
    const onMove = (ev) => {
      const dx = (ev.clientX - sx) / s;
      const dy = (ev.clientY - sy) / s;
      if (mode === "move") update(el.id, { x: snap(o.x + dx), y: snap(o.y + dy) });
      else
        update(el.id, {
          w: Math.max(2, snap(o.w + dx)),
          h: Math.max(el.type === "line" ? 0.3 : 2, snap(o.h + dy)),
        });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  const addElement = (type) => {
    const base = { id: newElementId(), override: null, x: W * 0.1, y: H * 0.35 };
    let el;
    if (type === "text")
      el = { ...base, type, w: W * 0.6, h: Math.min(8, H * 0.25), template: "", override: t("labels.designer.newText"), font: "sans", weight: 400, align: "left", fit: true, size: 12 };
    if (type === "barcode") el = { ...base, type, w: W * 0.5, h: Math.min(12, H * 0.4), template: "{nr}", showText: true };
    if (type === "qr") {
      const q = Math.min(H - 2 * SAFE_INSET_MM, 20);
      el = { ...base, type, x: W - SAFE_INSET_MM - q, y: SAFE_INSET_MM, w: q, h: q, template: "{url}" };
    }
    if (type === "line") el = { ...base, type, w: W * 0.8, h: 0.6, template: "" };
    setElements((els) => els.concat(el));
    setSelId(el.id);
  };

  const insertField = (key) => {
    if (selected && selected.type !== "line") {
      update(selected.id, (x) => {
        const keep = x.override == null ? x.template || "" : "";
        return { template: `${keep}${keep ? " " : ""}{${key}}`, override: null };
      });
      return;
    }
    const el = {
      id: newElementId(),
      type: "text",
      override: null,
      template: `{${key}}`,
      font: key === "nr" ? "mono" : "sans",
      weight: 400,
      align: "left",
      fit: true,
      size: 10,
      x: W * 0.1,
      y: H * 0.4,
      w: W * 0.6,
      h: Math.min(6, H * 0.2),
    };
    setElements((els) => els.concat(el));
    setSelId(el.id);
  };

  const duplicate = () => {
    if (!selected) return;
    const copy = { ...selected, id: newElementId(), x: selected.x + 2, y: selected.y + 2 };
    setElements((els) => els.concat(copy));
    setSelId(copy.id);
  };

  const doPrint = async (all) => {
    const list = all ? records : records[recIdx] ? [records[recIdx]] : [];
    if (!list.length || printing) return;
    setPrinting(true);
    setNotice(null);
    try {
      const count = await printLabels({
        elements,
        records: list,
        copies,
        W,
        H,
        title: t("labels.designer.title"),
      });
      setNotice({ kind: "ok", text: t("labels.designer.sent", { count, printer }) });
    } catch (_) {
      setNotice({ kind: "bad", text: t("labels.designer.printFailed") });
    } finally {
      setPrinting(false);
    }
  };

  const handleDialogClose = () => {
    // Esc first clears the selection, then closes.
    if (selId) setSelId(null);
    else onClose();
  };

  const overrideCount = elements.filter(
    (e) => e.override != null && e.type !== "line" && hasTokens(e.template)
  ).length;
  const radius = media.cont ? 0 : Math.min(3, 1.5 * scale);
  // A failed (re)load blocks printing so stale data is never sent to the printer.
  const canPrint = records.length > 0 && !loading && !loadError && !printing;
  const fieldLabel = (key) => t(`labels.fields.${key}`);
  const kindLabel = selected ? t(`labels.designer.kind.${selected.type}`) : "";
  const selText = selected ? elementText(selected, record) : "";
  const fittedPt =
    selected?.type === "text" && selected.fit && selText ? fittedFontMm(selected, selText) / PT_MM : null;

  return (
    <Transition.Root show={!!open} as={Fragment}>
      <Dialog as="div" className="relative" style={{ zIndex: 2000 }} onClose={handleDialogClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="pm-dialog__scrim fixed inset-0" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-hidden">
          <div className="flex min-h-full items-stretch justify-center sm:items-center sm:p-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="pm-dialog__panel label-designer" data-testid="label-designer">
                {/* Header */}
                <div className="label-designer__header">
                  <div className="min-w-0 flex-1">
                    <Dialog.Title as="h2" className="label-designer__title">
                      {t("labels.designer.title")}
                    </Dialog.Title>
                    <div className="label-designer__record">
                      <span className="label-designer__mono label-designer__accent">{record.nr || "…"}</span>
                      <span className="truncate">
                        {loading && !records.length ? t("labels.designer.loading") : record._title || record.navn || ""}
                      </span>
                      {loading && <span className="label-designer__spinner" aria-hidden="true" />}
                    </div>
                  </div>
                  <div className="label-designer__pager">
                    <IconButton
                      label={t("labels.designer.prev")}
                      disabled={records.length < 2}
                      onClick={() => setRecIdx((i) => (i - 1 + records.length) % records.length)}
                    >
                      <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
                    </IconButton>
                    <span className="label-designer__mono label-designer__pos" aria-live="polite">
                      {records.length ? `${recIdx + 1}/${records.length}` : "–"}
                    </span>
                    <IconButton
                      label={t("labels.designer.next")}
                      disabled={records.length < 2}
                      onClick={() => setRecIdx((i) => (i + 1) % records.length)}
                    >
                      <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
                    </IconButton>
                  </div>
                  <IconButton label={t("labels.designer.refresh")} onClick={fetchRecords} disabled={loading}>
                    <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
                  </IconButton>
                  <button type="button" className="pm-dialog__close" onClick={onClose} aria-label={t("labels.designer.close")}>
                    <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>

                {/* Printer / media toolbar */}
                <div className="label-designer__toolbar">
                  <Segmented
                    label={t("labels.designer.printer")}
                    options={PRINTERS.map((p) => ({ value: p, label: p }))}
                    value={printer}
                    onChange={choosePrinter}
                  />
                  <select
                    aria-label={t("labels.designer.media")}
                    className="label-designer__select"
                    value={mediaId}
                    onChange={(e) => chooseMedia(e.target.value)}
                  >
                    {mediaForPrinter(printer).map((m) => (
                      <option key={m.id} value={m.id}>
                        {mediaLabel(m, t)}
                      </option>
                    ))}
                  </select>
                  {media.cont && (
                    <label className="label-designer__unit-input">
                      <span className="sr-only">{t("labels.designer.length")}</span>
                      <input
                        type="number"
                        min={MIN_LENGTH_MM}
                        max={MAX_LENGTH_MM}
                        value={length}
                        onChange={(e) => setSetup({ ...setup, length: e.target.value })}
                        onBlur={() => setSetup({ ...setup, length: clampLength(length) })}
                      />
                      <span>mm</span>
                    </label>
                  )}
                  <span className="label-designer__hint">{t("labels.designer.browserPrintHint")}</span>
                  <span className="flex-1" />
                  <button
                    type="button"
                    className="pm-btn pm-btn--secondary pm-btn--sm"
                    title={t("labels.designer.autoLayoutHint")}
                    onClick={() => {
                      setElements(autoLayout(module, W, H));
                      setSelId(null);
                    }}
                  >
                    {t("labels.designer.autoLayout")}
                  </button>
                </div>

                {/* Body */}
                <div className="label-designer__body">
                  <div className="label-designer__stage">
                    <div className="label-designer__floating">
                      <div className="label-designer__tools" role="toolbar" aria-label={t("labels.designer.tools")}>
                        <IconButton label={t("labels.designer.kind.text")} onClick={() => addElement("text")}>
                          <span className="font-semibold">T</span>
                        </IconButton>
                        <IconButton label={t("labels.designer.kind.barcode")} onClick={() => addElement("barcode")}>
                          <span className="label-designer__bars" aria-hidden="true">
                            <span style={{ width: 1 }} />
                            <span style={{ width: 2 }} />
                            <span style={{ width: 1 }} />
                            <span style={{ width: 3 }} />
                            <span style={{ width: 1 }} />
                          </span>
                        </IconButton>
                        <IconButton label={t("labels.designer.kind.qr")} onClick={() => addElement("qr")}>
                          <QrCodeIcon className="h-4 w-4" aria-hidden="true" />
                        </IconButton>
                        <IconButton label={t("labels.designer.kind.line")} onClick={() => addElement("line")}>
                          <span className="label-designer__line-icon" aria-hidden="true" />
                        </IconButton>
                        <span className="label-designer__divider" />
                        <IconButton label={t("labels.designer.grid")} active={showGrid} onClick={() => setShowGrid((g) => !g)}>
                          <span className="label-designer__mono">#</span>
                        </IconButton>
                      </div>
                      <div className="label-designer__tools">
                        <IconButton label={t("labels.designer.zoomOut")} onClick={() => setZoom((z) => Math.max(0.25, +(z / 1.25).toFixed(3)))}>
                          <MinusIcon className="h-4 w-4" aria-hidden="true" />
                        </IconButton>
                        <button
                          type="button"
                          className="label-designer__zoom label-designer__mono"
                          title={t("labels.designer.zoomFit")}
                          onClick={() => setZoom(1)}
                        >
                          {Math.round(zoom * 100)}%
                        </button>
                        <IconButton label={t("labels.designer.zoomIn")} onClick={() => setZoom((z) => Math.min(4, +(z * 1.25).toFixed(3)))}>
                          <PlusIcon className="h-4 w-4" aria-hidden="true" />
                        </IconButton>
                      </div>
                    </div>

                    <div ref={setAreaNode} className="label-designer__canvas" onPointerDown={() => setSelId(null)}>
                      <div className="label-designer__canvas-inner">
                        {loadError ? (
                          <div className="pm-alert pm-alert--bad" role="alert">
                            <div>
                              <p className="font-semibold">{t("labels.designer.loadFailed")}</p>
                              <button type="button" className="pm-btn pm-btn--secondary pm-btn--sm mt-2" onClick={fetchRecords}>
                                {t("labels.designer.retry")}
                              </button>
                            </div>
                          </div>
                        ) : !loading && !records.length ? (
                          <p className="label-designer__hint" role="status">
                            {t("labels.designer.empty")}
                          </p>
                        ) : null}
                        <div
                          className="label-designer__label"
                          data-testid="label-canvas"
                          style={{
                            width: W * scale,
                            height: H * scale,
                            borderRadius: radius,
                            backgroundImage: showGrid
                              ? "linear-gradient(rgba(20,99,199,.08) 1px,transparent 1px),linear-gradient(90deg,rgba(20,99,199,.08) 1px,transparent 1px)"
                              : "none",
                            backgroundSize: `${5 * scale}px ${5 * scale}px`,
                          }}
                        >
                          <div
                            className="label-designer__safe"
                            style={{ inset: SAFE_INSET_MM * scale, borderRadius: Math.max(0, radius - 1) }}
                          />
                          {elements.map((el) => {
                            const isSel = el.id === selId;
                            const box = elementBoxStyle(el, scale);
                            if (el.type === "line") box.height = Math.max(el.h * scale, 6);
                            return (
                              <div
                                key={el.id}
                                data-testid={`label-el-${el.type}`}
                                className={`label-designer__el${isSel ? " is-selected" : ""}${showGrid ? " is-grid" : ""}`}
                                style={box}
                                onPointerDown={(e) => startDrag(e, el, "move")}
                                onDoubleClick={() => {
                                  setSelId(el.id);
                                  setTimeout(() => {
                                    textRef.current?.focus();
                                    textRef.current?.select();
                                  }, 30);
                                }}
                              >
                                <LabelElementContent el={el} record={record} s={scale} />
                                {el.override != null && el.type !== "line" && hasTokens(el.template) && (
                                  <span className="label-designer__override-dot" title={t("labels.designer.overridden")} />
                                )}
                                {isSel && (
                                  <span
                                    className="label-designer__resize"
                                    onPointerDown={(e) => startDrag(e, el, "resize")}
                                    aria-hidden="true"
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <span className="label-designer__mono label-designer__dim">
                          {round1(W)} × {round1(H)} mm ·{" "}
                          {media.cont ? t("labels.designer.feedContinuous") : t("labels.designer.feedDieCut")}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Inspector */}
                  <aside className="label-designer__panel" aria-label={t("labels.designer.inspector")}>
                    {selected ? (
                      <div className="flex flex-col">
                        <div className="label-designer__panel-head">
                          <span className="pm-eyebrow">{kindLabel}</span>
                          <div className="flex gap-1">
                            <IconButton label={t("labels.designer.duplicate")} onClick={duplicate}>
                              <DocumentDuplicateIcon className="h-4 w-4" aria-hidden="true" />
                            </IconButton>
                            <IconButton label={t("labels.designer.delete")} danger onClick={deleteSelected}>
                              <TrashIcon className="h-4 w-4" aria-hidden="true" />
                            </IconButton>
                          </div>
                        </div>

                        {selected.type !== "line" && (
                          <div className="label-designer__section">
                            <div className="flex items-center justify-between">
                              <span className="label-designer__section-title">{t("labels.designer.content")}</span>
                              {selected.override != null && hasTokens(selected.template) ? (
                                <span className="pm-badge pm-badge--warn">{t("labels.designer.manual")}</span>
                              ) : selected.override == null && hasTokens(selected.template) ? (
                                <span className="pm-badge pm-badge--accent">{t("labels.designer.fromData")}</span>
                              ) : null}
                            </div>
                            <textarea
                              ref={textRef}
                              rows={2}
                              aria-label={t("labels.designer.content")}
                              className="label-designer__textarea"
                              value={selText}
                              onChange={(e) => update(selected.id, { override: e.target.value })}
                            />
                            {selected.override != null && hasTokens(selected.template) && (
                              <div className="label-designer__override">
                                <span className="min-w-0 flex-1 truncate">
                                  {t("labels.designer.dataValue")}: {resolveTemplate(selected.template, record) || "—"}
                                </span>
                                <button type="button" onClick={() => update(selected.id, { override: null })}>
                                  {t("labels.designer.reset")}
                                </button>
                              </div>
                            )}
                            <input
                              aria-label={t("labels.designer.template")}
                              className="label-designer__input label-designer__mono"
                              value={selected.template || ""}
                              placeholder={t("labels.designer.templatePlaceholder")}
                              onChange={(e) => update(selected.id, { template: e.target.value, override: null })}
                            />
                            <div className="flex flex-wrap gap-1">
                              {fieldKeys.map((key) => (
                                <button
                                  key={key}
                                  type="button"
                                  className="label-designer__chip label-designer__mono"
                                  title={fieldLabel(key)}
                                  onClick={() => insertField(key)}
                                >
                                  {`{${key}}`}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {selected.type === "text" && (
                          <div className="label-designer__section">
                            <div className="grid grid-cols-[minmax(0,1fr)_76px] gap-1.5">
                              <select
                                aria-label={t("labels.designer.font")}
                                className="label-designer__select"
                                value={selected.font}
                                onChange={(e) => update(selected.id, { font: e.target.value })}
                              >
                                <option value="sans">Plex Sans</option>
                                <option value="cond">Plex Condensed</option>
                                <option value="mono">Plex Mono</option>
                              </select>
                              <label className="label-designer__unit-input">
                                <span className="sr-only">{t("labels.designer.size")}</span>
                                <input
                                  type="number"
                                  min="4"
                                  max="200"
                                  step="0.5"
                                  value={selected.size}
                                  onChange={(e) => {
                                    const v = parseFloat(e.target.value);
                                    if (!Number.isNaN(v)) update(selected.id, { size: v });
                                  }}
                                />
                                <span>pt</span>
                              </label>
                            </div>
                            <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-1.5">
                              <Segmented
                                label={t("labels.designer.weight")}
                                value={selected.weight >= 600 ? 700 : 400}
                                onChange={(w) => update(selected.id, { weight: w })}
                                options={[
                                  { value: 400, label: "N", title: t("labels.designer.weightNormal") },
                                  { value: 700, label: "B", title: t("labels.designer.weightBold") },
                                ]}
                              />
                              <Segmented
                                label={t("labels.designer.align")}
                                value={selected.align}
                                onChange={(a) => update(selected.id, { align: a })}
                                options={[
                                  { value: "left", label: "⇤", title: t("labels.designer.alignLeft") },
                                  { value: "center", label: "↔", title: t("labels.designer.alignCenter") },
                                  { value: "right", label: "⇥", title: t("labels.designer.alignRight") },
                                ]}
                              />
                            </div>
                            <label className="flex cursor-pointer items-center gap-2 text-[12.5px]">
                              <input
                                type="checkbox"
                                className="pm-checkbox"
                                checked={!!selected.fit}
                                onChange={(e) => update(selected.id, { fit: e.target.checked })}
                              />
                              <span>{t("labels.designer.shrinkToFit")}</span>
                              <span className="flex-1" />
                              {fittedPt != null && fittedPt < selected.size - 0.05 && (
                                <span className="label-designer__mono label-designer__muted">→ {fittedPt.toFixed(1)} pt</span>
                              )}
                            </label>
                          </div>
                        )}

                        {selected.type === "barcode" && (
                          <div className="label-designer__section">
                            <label className="flex cursor-pointer items-center gap-2 text-[12.5px]">
                              <input
                                type="checkbox"
                                className="pm-checkbox"
                                checked={!!selected.showText}
                                onChange={(e) => update(selected.id, { showText: e.target.checked })}
                              />
                              <span className="flex-1">{t("labels.designer.showText")}</span>
                              <span className="label-designer__muted text-[11px]">
                                {t("labels.designer.code39Info", { count: code39Data(selText).length })}
                              </span>
                            </label>
                            {selText && code39Data(selText).length < selText.replace(/\s/g, "").length && (
                              <p className="label-designer__muted text-[11.5px]">{t("labels.designer.code39Dropped")}</p>
                            )}
                          </div>
                        )}

                        <div className="label-designer__geom">
                          {[
                            ["X", "x"],
                            ["Y", "y"],
                            [t("labels.designer.widthShort"), "w"],
                            [t("labels.designer.heightShort"), "h"],
                          ].map(([label, key]) => (
                            <label key={key} className="label-designer__unit-input">
                              <span className="label-designer__geom-label label-designer__mono">{label}</span>
                              <input
                                type="number"
                                step="0.5"
                                aria-label={t(`labels.designer.geom.${key}`)}
                                value={round1(+selected[key])}
                                onChange={(e) => {
                                  const v = parseFloat(e.target.value);
                                  if (!Number.isNaN(v)) update(selected.id, { [key]: v });
                                }}
                              />
                            </label>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col">
                        <div className="label-designer__panel-head">
                          <span className="pm-eyebrow">{t("labels.designer.fields")}</span>
                          <span className="label-designer__muted text-[11px]">{t("labels.designer.clickToInsert")}</span>
                        </div>
                        <div className="p-1.5">
                          {fieldKeys.map((key) => (
                            <button
                              key={key}
                              type="button"
                              className="label-designer__field"
                              title={t("labels.designer.newField", { field: fieldLabel(key) })}
                              onClick={() => insertField(key)}
                            >
                              <span className="label-designer__mono label-designer__accent text-[11px]">{`{${key}}`}</span>
                              <span className="truncate">{record[key] || <span className="label-designer__muted">{fieldLabel(key)}</span>}</span>
                              <PlusIcon className="h-3 w-3 label-designer__muted" aria-hidden="true" />
                            </button>
                          ))}
                        </div>
                        {overrideCount > 0 && (
                          <div className="pm-alert pm-alert--warn mx-3.5 mb-3.5 text-[12px]">
                            {t("labels.designer.overrideCount", { count: overrideCount })}
                          </div>
                        )}
                      </div>
                    )}
                  </aside>
                </div>

                {/* Footer */}
                <div className="label-designer__footer">
                  <span className="label-designer__mono label-designer__muted mr-auto text-[11px]">
                    Brother {printer} · {mmToDots(W)} × {mmToDots(H)} {t("labels.designer.dots")} · {PRINTER_DPI} dpi
                  </span>
                  {notice && (
                    <span
                      role={notice.kind === "bad" ? "alert" : "status"}
                      className={`pm-badge ${notice.kind === "bad" ? "pm-badge--bad" : "pm-badge--ok"}`}
                    >
                      {notice.text}
                    </span>
                  )}
                  <label className="label-designer__unit-input label-designer__copies">
                    <span className="label-designer__muted pl-2 text-[12px]">{t("labels.designer.copies")}</span>
                    <input
                      type="number"
                      min="1"
                      max="99"
                      value={copies}
                      onChange={(e) => setCopies(e.target.value)}
                      onBlur={() => setCopies(Math.max(1, Math.min(99, parseInt(copies, 10) || 1)))}
                    />
                  </label>
                  {records.length > 1 && (
                    <button
                      type="button"
                      className="pm-btn pm-btn--secondary pm-btn--sm"
                      disabled={!canPrint}
                      onClick={() => doPrint(true)}
                    >
                      {t("labels.designer.printAll", { count: records.length })}
                    </button>
                  )}
                  <button type="button" className="pm-btn pm-btn--ghost pm-btn--sm" onClick={onClose}>
                    {t("labels.designer.cancel")}
                  </button>
                  <button
                    type="button"
                    className="pm-btn pm-btn--primary pm-btn--sm"
                    disabled={!canPrint}
                    aria-busy={printing}
                    onClick={() => doPrint(false)}
                  >
                    {printing && <span className="pm-btn__spinner" aria-hidden="true" />}
                    {t("labels.designer.print")}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

LabelDesignerDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  module: PropTypes.oneOf(["product", "project", "report", "tool", "document"]),
  loadRecords: PropTypes.func.isRequired,
};

export default LabelDesignerDialog;
